import { useContext, useMemo } from 'react';
import { isQuickFilterEditorSchema, isUserAddedQuickFilterEditorSchema } from 'reporting-data/tsTypes/editorTypes';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import GoalsKPIUnitTypeEditor from '../GoalEditor/GoalsKPIUnitTypeEditor';
import { getWidgetEditors } from '../getWidgetEditors';
import { editorMatchesCategory, getEditorSchemasFromConfig } from './editorUtils';
import { useDynamicallyGeneratedEditorSchemas, useDynamicallyGeneratedReportEditors, useEditorComponents, useEditorsFromConfig } from './useEditorComponents';
import { getCustomFilterProperties, getReportConfig } from 'reporting-data/report/configReportGetters';
import { isConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { areORFiltersSupported } from '../Filters/filtersUtils';
import { MAX_CUSTOM_QUICK_FILTERS } from '../Filters/ReportingSegmentationFiltersBar';
export const useEditorsAndFilters = (report, previewReport, editorOverrides) => {
  const {
    gates = [],
    user: {
      scopes = []
    }
  } = useContext(UserInfoContext);
  const dynamicFilters = useDynamicallyGeneratedReportEditors(previewReport);
  const usableWidgetEditors = getWidgetEditors(report).filter(Editor => Editor.isUsableForReport(previewReport, gates, scopes));
  const editorsFromConfig = useEditorsFromConfig(report);
  return useMemo(() => {
    const dateEditors = usableWidgetEditors.filter(editor => editor.isDateEditor);
    const remainingEditors = usableWidgetEditors.filter(editor => !editor.isDateEditor);
    return editorOverrides || [...dateEditors, ...editorsFromConfig, ...dynamicFilters, ...remainingEditors];
  }, [dynamicFilters, editorOverrides, editorsFromConfig, usableWidgetEditors]);
};
export const useFilters = report => {
  const {
    gates,
    user: {
      scopes
    }
  } = useContext(UserInfoContext);
  // Set of static editors that render before the Filters Bar
  const filterEditors = [...getWidgetEditors(report), GoalsKPIUnitTypeEditor].filter(Editor => Editor.isUsableForReport(report, gates, scopes) && Editor.isFilterEditor);
  const dynamicFiltersSchema = useDynamicallyGeneratedEditorSchemas(report);
  const editorSchemaFromConfig = getEditorSchemasFromConfig(report);
  const filterSchema = [...dynamicFiltersSchema, ...editorSchemaFromConfig];
  const userAddedFilterProperties = useMemo(() => {
    let customFiltersProperties = [];
    if (isConfigReport(report) && areORFiltersSupported(report)) {
      // In case the OR filters are supported, the custom filters are not editable w/o having a quick filter
      // editor.  This logic ensures all custom filters have a corresponding quick filter editor.
      customFiltersProperties = getCustomFilterProperties(getReportConfig(report)).toJS();
    }
    const editorProperties = editorSchemaFromConfig.reduce((memo, schema) => {
      if (isUserAddedQuickFilterEditorSchema(schema)) {
        memo.push(schema.property);
      }
      return memo;
    }, Array());
    const filterProperties = [...new Set([...editorProperties, ...customFiltersProperties])];

    // Note that MAX_CUSTOM_QUICK_FILTERS should provide sufficient number of quick filters
    // In rare cases, the following logic will drop any custom quick filters that exceed the
    // max and result into an issue where a custom filter exists w/o a corresponding quick filter editor.
    return filterProperties.slice(0, MAX_CUSTOM_QUICK_FILTERS);
  }, [editorSchemaFromConfig, report]);
  return {
    filterEditors,
    filterProperties: filterSchema.reduce((memo, schema) => {
      if (isQuickFilterEditorSchema(schema)) {
        memo.push(schema.property);
      }
      return memo;
    }, Array()),
    userAddedFilterProperties
  };
};
export const useEditors = (report, category) => {
  const {
    gates,
    user: {
      scopes
    }
  } = useContext(UserInfoContext);
  const editors = getWidgetEditors(report).filter(Editor => Editor.isUsableForReport(report, gates, scopes) && !Editor.isFilterEditor && editorMatchesCategory(Editor, category));
  const dynamicFiltersSchema = useDynamicallyGeneratedEditorSchemas(report);
  const editorSchemaFromConfig = getEditorSchemasFromConfig(report);
  const editorSchema = [...dynamicFiltersSchema, ...editorSchemaFromConfig].filter(schema => !isQuickFilterEditorSchema(schema) && !isUserAddedQuickFilterEditorSchema(schema));
  const editorsWithSchema = useEditorComponents(report, editorSchema, undefined, category);
  return [...editors, ...editorsWithSchema];
};